import { CommonService } from '@business/services';
import { Endpoint } from '../endpoint';
import {
  EstimateGroupLineItemData,
  EstimateItemFormData,
  EstimateItemGroupData,
} from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';
import {
  EstimateSearchParams,
  TMEstimateId,
  TMEstimateResponse,
  CompactTMEstimateResponse,
  TMEstimateGroupId,
  TMEstimateLineItemResponse,
  VeryCompactTMEstimateResponse,
  TMEstimateLineItemId,
  TMEstimateFeeId,
  TMEstimateDefaultEstimatorResponse,
  EstimateItemNoteResponse,
} from '@business/entities/tm';
import { UserId } from '@business/entities/account';
import { AddEstimateFormData } from '@modules/organizations/OrganizationPage/Components/Design/AddEstimateModal/AddEstimateModal';
import { R } from '@core/utils/r';
import { OrgId } from '@business/entities/organization';
import { CategoryOptionId } from '@business/entities/category_option';
import { ServiceProviderId } from '@business/entities/tm/TmServiceProviderResponse';
import { EstimateFilter } from '@modules/organizations/OrganizationPage/Components/DesignV2/DesignTabV2Bloc';

export function fetchEstimates(orgId?: OrgId, isCompact = false, filters?: EstimateFilter) {
  const params = R.objectCompact({ orgId, isCompact, ...filters });
  // if(isCompact){
  //   return Endpoint.get<CompactTMEstimateResponse[]>(`@gc/estimates`, { params });
  // }
  return Endpoint.get<TMEstimateResponse[]>(`@gc/estimates-v2`, { params });
}

export function fetchEstimatesPaginated(params: EstimateSearchParams) {
  return Endpoint.get<CompactTMEstimateResponse[]>(`@gc/estimates-v2/estimates-paginated`, { params: R.objectCompact(params) });
}

export function fetchEstimate(id: TMEstimateId) {
  return Endpoint.get<TMEstimateResponse>(`@gc/estimates-v2/estimate/${id}`);
}

export function createEstimate(data: AddEstimateFormData) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2`, { formData, hasFiles: true });
}

export function recalculateEstimate(id: TMEstimateId) {
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/recalculate/${id}`);
}

export function updateEstimate(id: TMEstimateId, data: EstimateItemFormData) {
  return Endpoint.patch<TMEstimateResponse>(`@gc/estimates-v2/${id}`, { data });
}

export function deleteEstimate(id: TMEstimateId) {
  return Endpoint.delete<void>(`@gc/estimates-v2/${id}`);
}

export function restoreEstimate(id: TMEstimateId) {
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/restore/${id}`);
}

export function addOrUpdateGroup(id: TMEstimateId, data: EstimateItemGroupData) {
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/group/${id}`, { data });
}

export function updateWeProvide(groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) {
  const data = R.objectCompact({ ...item, groupId });
  // Returns the whole estimate: the provider Minimum Invoice lines follow the We Provide change
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/update-item`, { data });
}

export function qtyUpdate(groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) {
  const data = R.objectCompact({ ...item, groupId });
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/qty-update-item`, { data });
}

export function priceUpdate(groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) {
  const data = R.objectCompact({ ...item, groupId });
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/update-item-price`, { data });
}

export function getEstimateTitle(orgId: OrgId) {
  return Endpoint.get<{ title: string }>(`@gc/estimates-v2/title/${orgId}`);
}

export function fetchSubProjects(estimateId: TMEstimateId) {
  return Endpoint.get<VeryCompactTMEstimateResponse[]>(`@gc/estimates-v2/sub-projects`, { params: { parentId: estimateId } });
}

export function updateOption(groupId: TMEstimateGroupId, optionId: CategoryOptionId, providerId: ServiceProviderId) {
  const data = R.objectCompact({ optionId, providerId });
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/update-option/${groupId}`, { data });
}

export function updateNote(itemId: TMEstimateLineItemId, note: string) {
  const data = { note };
  return Endpoint.patch<void>(`@gc/estimates-v2/update-note/${itemId}`, { data });
}

export function updateFee(id: TMEstimateFeeId, value: number) {
  const data = R.objectCompact({ value });
  return Endpoint.post<TMEstimateResponse>(`@gc/estimates-v2/update-fee/${id}`, { data });
}

// The estimate's estimator (estimate page avatar); null removes it. Needs "Change Estimate Estimator"
export function updateEstimator(id: TMEstimateId, estimatorUserId: UserId | null) {
  return Endpoint.patch<TMEstimateResponse>(`@gc/estimates-v2/estimator/${id}`, { data: { estimatorUserId } });
}

// The Create Estimate Title: "EST-{next number} - {customer} - {today} - {estimator}" (the number is confirmed on save)
export function fetchDefaultName(orgId: OrgId, estimatorUserId?: UserId | null) {
  return Endpoint.get<{ title: string }>(`@gc/estimates-v2/default-name/${orgId}`, { params: R.objectCompact({ estimatorUserId }) });
}

// The estimator a new estimate for this customer starts with (its newest in-progress deal's), or null
export function fetchDefaultEstimator(orgId: OrgId) {
  return Endpoint.get<TMEstimateDefaultEstimatorResponse>(`@gc/estimates-v2/default-estimator/${orgId}`);
}

// A line's notes history, newest first (price changes with their reason, manual quantities, erases, notes)
export function fetchItemNotes(itemId: TMEstimateLineItemId) {
  return Endpoint.get<EstimateItemNoteResponse[]>(`@gc/estimates-v2/item-notes/${itemId}`);
}

// Adds a note by hand to a line's notes history
export function addItemNote(itemId: TMEstimateLineItemId, note: string) {
  return Endpoint.post<EstimateItemNoteResponse>(`@gc/estimates-v2/item-notes/${itemId}`, { data: { note } });
}

// An option's description (Options tab): only the description changes, nothing is recalculated
export function updateDescription(id: TMEstimateId, description: string) {
  return Endpoint.patch<TMEstimateResponse>(`@gc/estimates-v2/description/${id}`, { data: { description } });
}
